import React, { useEffect, useMemo, useRef, useState } from 'react'
import { format, parseISO } from 'date-fns'
import {
  Header,
  Loadable,
  NavFilter,
  Pagination,
  TableHead,
  navigateTo,
  useApiGet,
  useModal,
  useSortableTable,
  varClass,
} from '../shared'
import {
  IRejectionReviewAdvertiserMonth,
  IRejectionReviewAdvertiserQueueRow,
  IRejectionReviewAdvertisersResponse,
  IRejectionReviewListResponse,
  IRejectionReviewMonthRow,
  IRejectionReviewSummary,
  IRejectionReviewTrendResponse,
  TAnomaliesSuppressedMode,
  TRejectionReviewIssueType,
} from '../shared/types'
import { getRequest } from '../../services/api'
import { RejectionTrendChart } from './charts/RejectionTrendChart'
import { SuppressModal } from './SuppressModal'
import {
  FilterGroup,
  NetworkScope,
  NetworkScopePill,
  applyNetworkScopeParams,
  europeanizeNumbers,
  formatDecimal,
  formatDkkLarge,
  formatInteger,
  useAnomaliesNetworks,
} from './shared'
import { PresetsControl, findDefaultPreset, useUserFilters } from './presets'
import { UserFilterValues } from './api'

// FE-derived maturity. The backend no longer ships a maturityStatus field —
// rejections can land long after the month ends so any "settled" gate would
// lie. The only meaningful boundary now is "current month is still
// accumulating" vs "month is in the past". We use this in two places:
//   1. To colour the trend chart's stacked bars so the operator can see what
//      portion of a month is still in motion.
//   2. To render an "Open" badge on the current month so it reads as
//      preliminary rather than final.
export type RejectionMaturity = 'open' | 'completed'

export const deriveMaturity = (daysSinceMonthEnd: number): RejectionMaturity =>
  daysSinceMonthEnd < 0 ? 'open' : 'completed'

// Conditional GET. Skips the network call entirely while `enabled` is false
// so toggling between the Advertisers and Monthly-rows views doesn't double
// up requests on the initial load. When `enabled` flips back to true the
// fetch fires. Mirrors the useApiGet contract (returns null until loaded,
// then the unwrapped data payload).
const useApiGetIf = <T,>(
  enabled: boolean,
  path: string,
  params: any,
  deps: any[]
): T | null => {
  const [data, setData] = useState<T | null>(null)
  // Key on the caller's deps (not just params) so a bumped refetch counter
  // re-fires the request even when the querystring is unchanged.
  const key = useMemo(
    () => (enabled ? `${path}?${JSON.stringify(deps)}` : null),
    [enabled, path, ...deps]
  )
  useEffect(() => {
    if (!enabled) {
      setData(null)
      return
    }
    let cancelled = false
    setData(null)
    getRequest(path, params).then((resp) => {
      if (cancelled) return
      setData((resp?.data as T) ?? ({} as T))
    })
    return () => {
      cancelled = true
    }
  }, [key])
  return data
}

// Rejection review — monthly cohort view driven by conversion month.
// Separate from daily anomaly triage on purpose (rejection is retroactive).
// Defaults: last 3 months (end = previous month), action-required only
// (= flagged), advertiser-grouped view. The user narrows further via the
// date range; there's no implicit maturity gate.

const STATE_KEY = 'rejection-review-state-v2'

type View = 'advertisers' | 'months'

const ISSUE_TYPE_LABELS: Record<TRejectionReviewIssueType, string> = {
  commercial_impact: 'Commercial impact',
  high_rejection_rate: 'High rejection rate',
  repeated_high_rejection: 'Repeated high rejection',
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
const monthsAgo = (n: number): string => {
  const d = new Date()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() - n)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

const previousMonth = (): string => {
  const d = new Date()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() - 1)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

const formatMonth = (ym: string): string => {
  try {
    const iso = ym.length === 10 ? ym : `${ym}-01`
    return format(parseISO(iso), 'MMM yyyy')
  } catch {
    return ym
  }
}

const formatPercent1 = (rate: number | null | undefined): string => {
  if (rate == null || isNaN(rate)) return '—'
  return `${formatDecimal(rate * 100, 1)}%`
}

const formatDkkWhole = (dkk: number | null | undefined): string => {
  if (dkk == null || isNaN(dkk)) return '—'
  return `${formatInteger(Math.round(dkk))} DKK`
}

// ---------------------------------------------------------------------------
// Session snapshot
// ---------------------------------------------------------------------------
type Snapshot = {
  view: View
  startMonth: string
  endMonth: string
  search: string
  issueType: string
  showAll: boolean
  device: string
  minSaleDkk: string
  minSaleRate: string
  networkScope: NetworkScope
  customNetworkUuids: string[]
  advertisersSort: string
  advertisersDir: 'asc' | 'desc'
  monthsSort: string
  monthsDir: 'asc' | 'desc'
  page: number
  pageSize: number
  scrollY: number
}

const DEFAULTS: Snapshot = {
  view: 'advertisers',
  startMonth: monthsAgo(3),
  endMonth: previousMonth(),
  search: '',
  issueType: '',
  showAll: false,
  device: '',
  minSaleDkk: '',
  minSaleRate: '',
  networkScope: 'all',
  customNetworkUuids: [],
  advertisersSort: 'totalRejectedSaleDkk',
  advertisersDir: 'desc',
  monthsSort: 'conversionMonth',
  monthsDir: 'desc',
  page: 1,
  pageSize: 10,
  scrollY: 0,
}

const readSnapshot = (): Snapshot => {
  if (typeof window === 'undefined') return DEFAULTS
  try {
    const raw = window.sessionStorage.getItem(STATE_KEY)
    if (!raw) return DEFAULTS
    return { ...DEFAULTS, ...JSON.parse(raw) }
  } catch {
    return DEFAULTS
  }
}

const writeSnapshot = (patch: Partial<Snapshot>) => {
  if (typeof window === 'undefined') return
  try {
    const cur = readSnapshot()
    window.sessionStorage.setItem(
      STATE_KEY,
      JSON.stringify({ ...cur, ...patch })
    )
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const RejectionReview = () => {
  const initial = useRef<Snapshot>(readSnapshot()).current

  // Was there a sessionStorage snapshot when the tab booted? Used to gate the
  // first-visit auto-apply of the default preset — we don't override a
  // mid-task session with a preset they may not want right now.
  const hadSnapshotOnMount = useRef<boolean>(
    typeof window !== 'undefined' &&
      !!window.sessionStorage.getItem(STATE_KEY)
  ).current

  const [view, setView] = useState<View>(initial.view)
  const [startMonth, setStartMonth] = useState<string>(initial.startMonth)
  const [endMonth, setEndMonth] = useState<string>(initial.endMonth)
  const [searchInput, setSearchInput] = useState<string>(initial.search)
  const [appliedSearch, setAppliedSearch] = useState<string>(initial.search)
  useEffect(() => {
    const t = window.setTimeout(() => setAppliedSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(t)
  }, [searchInput])

  const [issueType, setIssueType] = useState<string>(initial.issueType)
  const [showAll, setShowAll] = useState<boolean>(initial.showAll)
  const [device, setDevice] = useState<string>(initial.device)
  const [minSaleDkk, setMinSaleDkk] = useState<string>(initial.minSaleDkk)
  const [minSaleRate, setMinSaleRate] = useState<string>(initial.minSaleRate)
  const [networkScope, setNetworkScope] = useState<NetworkScope>(
    initial.networkScope
  )
  const [customNetworkUuids, setCustomNetworkUuids] = useState<string[]>(
    initial.customNetworkUuids
  )
  const { networks, heylinkUuid } = useAnomaliesNetworks()

  // How 'rejections'-scoped suppressions apply to the trend / list /
  // advertisers endpoints. Like triage, intentionally not persisted in the
  // session snapshot — a fresh visit lands back on the clean default view.
  const [suppressedMode, setSuppressedMode] =
    useState<TAnomaliesSuppressedMode>('exclude')

  // Bumped after a hide-from-review action so all three suppression-aware
  // queries refetch in lockstep.
  const [suppressionRefetchKey, setSuppressionRefetchKey] = useState(0)

  // Two sortable tables (queue + monthly rows) keep independent state so
  // toggling the view doesn't lose the user's chosen sort.
  const advertisersSortState = useSortableTable({
    sort: initial.advertisersSort,
    dir: initial.advertisersDir,
  })
  const monthsSortState = useSortableTable({
    sort: initial.monthsSort,
    dir: initial.monthsDir,
  })

  const [page, setPage] = useState<number>(initial.page)
  const [pageSize, setPageSize] = useState<number>(initial.pageSize)

  const skipPageReset = useRef<boolean>(true)
  useEffect(() => {
    if (skipPageReset.current) {
      skipPageReset.current = false
      return
    }
    setPage(1)
  }, [
    view,
    startMonth,
    endMonth,
    appliedSearch,
    issueType,
    showAll,
    device,
    minSaleDkk,
    minSaleRate,
    networkScope,
    customNetworkUuids,
    suppressedMode,
    pageSize,
  ])

  useEffect(() => {
    writeSnapshot({
      view,
      startMonth,
      endMonth,
      search: searchInput,
      issueType,
      showAll,
      device,
      minSaleDkk,
      minSaleRate,
      networkScope,
      customNetworkUuids,
      advertisersSort: advertisersSortState.sort,
      advertisersDir: advertisersSortState.dir,
      monthsSort: monthsSortState.sort,
      monthsDir: monthsSortState.dir,
      page,
      pageSize,
    })
  }, [
    view,
    startMonth,
    endMonth,
    searchInput,
    issueType,
    showAll,
    device,
    minSaleDkk,
    minSaleRate,
    networkScope,
    customNetworkUuids,
    advertisersSortState.sort,
    advertisersSortState.dir,
    monthsSortState.sort,
    monthsSortState.dir,
    page,
    pageSize,
  ])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (initial.scrollY > 0) window.scrollTo(0, initial.scrollY)
    const onScroll = () => writeSnapshot({ scrollY: window.scrollY })
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [initial.scrollY])

  // Shared filter params for /advertisers and /list (+ derived trendParams).
  const baseParams = useMemo(() => {
    const p: Record<string, string | number> = {
      start_month: startMonth,
      end_month: endMonth,
      suppressed: suppressedMode,
    }
    if (appliedSearch) p.advertiser_domain = appliedSearch
    if (issueType) p.issue_type = issueType
    if (device) p.device = device
    const dkk = Number(minSaleDkk)
    if (!isNaN(dkk) && dkk > 0) p.min_rejected_sale_dkk = dkk
    const rate = Number(minSaleRate)
    if (!isNaN(rate) && rate > 0) {
      p.min_rejected_sale_rate = Math.min(rate / 100, 1)
    }
    return applyNetworkScopeParams(p, networkScope, heylinkUuid, customNetworkUuids, networks)
  }, [
    startMonth,
    endMonth,
    suppressedMode,
    appliedSearch,
    issueType,
    device,
    minSaleDkk,
    minSaleRate,
    networkScope,
    heylinkUuid,
    customNetworkUuids,
    networks,
  ])

  // ----- Saved filter presets ----------------------------------------------
  // Captures the filter knobs only. start_month / end_month are *not* part of
  // the preset — operators expect "Direct partners" to mean the latest data,
  // not a frozen point in time.
  const presetValues = useMemo<UserFilterValues>(() => {
    const v: UserFilterValues = {}
    if (appliedSearch) v.advertiser_domain = appliedSearch
    if (issueType) v.issue_type = issueType
    if (device) v.device = device
    if (showAll) v.show_all = 'true'
    const dkk = Number(minSaleDkk)
    if (!isNaN(dkk) && dkk > 0) v.min_rejected_sale_dkk = dkk
    const rate = Number(minSaleRate)
    if (!isNaN(rate) && rate > 0) v.min_rejected_sale_rate = rate
    if (networkScope !== 'all') v.network_scope = networkScope
    if (networkScope === 'custom' && customNetworkUuids.length > 0) {
      v.custom_networks = customNetworkUuids.join(',')
    }
    if (suppressedMode !== 'exclude') v.suppressed = suppressedMode
    if (view !== 'advertisers') v.view = view
    return v
  }, [
    appliedSearch,
    issueType,
    device,
    showAll,
    minSaleDkk,
    minSaleRate,
    networkScope,
    customNetworkUuids,
    suppressedMode,
    view,
  ])

  const applyPresetValues = (values: UserFilterValues) => {
    const q = values.advertiser_domain != null ? String(values.advertiser_domain) : ''
    setSearchInput(q)
    setAppliedSearch(q)
    setIssueType(values.issue_type != null ? String(values.issue_type) : '')
    setDevice(values.device != null ? String(values.device) : '')
    setShowAll(String(values.show_all ?? '') === 'true')
    setMinSaleDkk(values.min_rejected_sale_dkk != null ? String(values.min_rejected_sale_dkk) : '')
    setMinSaleRate(values.min_rejected_sale_rate != null ? String(values.min_rejected_sale_rate) : '')
    const scope = values.network_scope
    if (scope === 'direct' || scope === 'affiliate' || scope === 'custom') {
      setNetworkScope(scope)
    } else {
      setNetworkScope('all')
    }
    if (values.custom_networks != null) {
      setCustomNetworkUuids(String(values.custom_networks).split(',').filter(Boolean))
    } else {
      setCustomNetworkUuids([])
    }
    const sup = values.suppressed
    if (sup === 'only' || sup === 'include' || sup === 'exclude') {
      setSuppressedMode(sup)
    } else {
      setSuppressedMode('exclude')
    }
    const v = values.view
    if (v === 'months' || v === 'advertisers') setView(v)
    setPage(1)
  }

  const presetsState = useUserFilters('rejection_review')

  const autoAppliedRef = useRef<boolean>(hadSnapshotOnMount)
  useEffect(() => {
    if (autoAppliedRef.current) return
    if (!presetsState.presets) return
    autoAppliedRef.current = true
    const def = findDefaultPreset(presetsState.presets)
    if (def) applyPresetValues(def.values)
  }, [presetsState.presets])

  // /trend bundles both the per-month rows for the chart and the range-level
  // totals for the summary cards. Filter params are intentionally not sent —
  // the trend is a context view that always reflects the underlying activity
  // for the date range, not the user's narrowed query.
  const trendParams = useMemo(
    () =>
      applyNetworkScopeParams(
        {
          start_month: startMonth,
          end_month: endMonth,
          suppressed: suppressedMode,
        },
        networkScope,
        heylinkUuid,
        customNetworkUuids,
        networks
      ),
    [startMonth, endMonth, suppressedMode, networkScope, heylinkUuid, customNetworkUuids, networks]
  )
  const trend = useApiGet<IRejectionReviewTrendResponse>(
    '/anomalies/v2/rejection-review/trend',
    trendParams,
    [JSON.stringify(trendParams), suppressionRefetchKey]
  )
  const summary: IRejectionReviewSummary | null = trend?.rangeTotals ?? null

  // Backend defaults (advertisers=1000, list=5000) cover any realistic range,
  // so we fetch the full dataset once per filter change and paginate / sort
  // client-side. `page` and `pageSize` are pure presentation state — they
  // don't appear in the request, which means sort header clicks and page-size
  // changes are instant with no refetch.
  const advertisersParams = useMemo(() => {
    const p: Record<string, string | number> = { ...baseParams }
    if (showAll) p.show_all = 'true'
    return p
  }, [baseParams, showAll])

  // Only the active view's table fetches data — the inactive view sits as a
  // null and re-fetches lazily when the user toggles.
  const advertisers = useApiGetIf<IRejectionReviewAdvertisersResponse>(
    view === 'advertisers',
    '/anomalies/v2/rejection-review/advertisers',
    advertisersParams,
    [JSON.stringify(advertisersParams), suppressionRefetchKey]
  )

  const listParams = useMemo(() => ({ ...baseParams }), [baseParams])
  const list = useApiGetIf<IRejectionReviewListResponse>(
    view === 'months',
    '/anomalies/v2/rejection-review/list',
    listParams,
    [JSON.stringify(listParams), suppressionRefetchKey]
  )

  const focusMonth = (ym: string) => {
    setStartMonth(ym)
    setEndMonth(ym)
  }

  // The trend chart highlights a single month when the range is collapsed
  // (start == end), matching the "focused month" reference line on the
  // detail page.
  const selectedTrendMonth = startMonth === endMonth ? startMonth : undefined

  const openAdvertiser = (advertiserDomain: string, e: React.MouseEvent) => {
    navigateTo(
      e,
      `/anomalies/rejection-review/${encodeURIComponent(advertiserDomain)}`
    )
  }

  // "Hide from rejection review" — a suppression scoped to 'rejections'. The
  // modal keeps its scope selector so the operator can still widen it, but
  // arrives pre-set. On success all three queries refetch; no optimistic row
  // removal here (the tables reload quickly and the Loadable state is honest).
  const { showModal, hideModal } = useModal()
  const openHideFromReviewModal = (advertiserDomain: string) => {
    showModal(
      <SuppressModal
        advertiserDomain={advertiserDomain}
        initialScope="rejections"
        hideModal={hideModal}
        onSuppressed={() => setSuppressionRefetchKey((k) => k + 1)}
      />
    )
  }

  const resetFilters = () => {
    setStartMonth(DEFAULTS.startMonth)
    setEndMonth(DEFAULTS.endMonth)
    setSearchInput('')
    setAppliedSearch('')
    setIssueType('')
    setShowAll(false)
    setDevice('')
    setMinSaleDkk('')
    setMinSaleRate('')
    setNetworkScope(DEFAULTS.networkScope)
    setCustomNetworkUuids(DEFAULTS.customNetworkUuids)
    setSuppressedMode('exclude')
  }

  const applyCardFilter = (next: { issueType?: string }) => {
    if (next.issueType !== undefined) {
      setIssueType(next.issueType)
      // repeated_high_rejection is an advertiser-level signal — no single
      // month row carries that flag, so applying it on Monthly Rows view
      // would return zero results. Flip to Advertisers automatically.
      if (next.issueType === 'repeated_high_rejection' && view !== 'advertisers') {
        setView('advertisers')
      }
    }
  }

  return (
    <div className="anomalies-page">
      <Header label="Rejection review" />

      <div className="m-t-4">
        <RejectionTrendChart
          rows={trend?.rows ?? null}
          selectedMonth={selectedTrendMonth}
          onSelectMonth={focusMonth}
        />
      </div>

      <Loadable data={summary}>
        {summary && (
          <SummaryCards
            summary={summary}
            activeIssueType={issueType}
            onApply={applyCardFilter}
          />
        )}
      </Loadable>

      <FiltersBar
        startMonth={startMonth}
        endMonth={endMonth}
        onMonthRangeChange={(start, end) => {
          setStartMonth(start)
          setEndMonth(end)
        }}
        searchInput={searchInput}
        onSearch={setSearchInput}
        issueType={issueType}
        onIssueTypeChange={setIssueType}
        showAll={showAll}
        onShowAllChange={setShowAll}
        device={device}
        onDeviceChange={setDevice}
        minSaleDkk={minSaleDkk}
        onMinSaleDkkChange={setMinSaleDkk}
        minSaleRate={minSaleRate}
        onMinSaleRateChange={setMinSaleRate}
        networkScope={networkScope}
        onNetworkScopeChange={setNetworkScope}
        customNetworkUuids={customNetworkUuids}
        onCustomNetworkUuidsChange={setCustomNetworkUuids}
        suppressedMode={suppressedMode}
        onSuppressedModeChange={setSuppressedMode}
        onReset={resetFilters}
        view={view}
        onViewChange={setView}
        presetValues={presetValues}
        applyPresetValues={applyPresetValues}
        presetsState={presetsState}
      />

      {suppressedMode === 'only' && (
        <div className="anomalies-suppressed-only-banner m-t-3">
          <span>
            <b>Showing suppressed only.</b> These advertisers are currently
            hidden from the rejection review.
          </span>
          <button
            type="button"
            className="anomalies-suppressed-banner-back"
            onClick={() => setSuppressedMode('exclude')}
          >
            Back to default
          </button>
        </div>
      )}

      {view === 'advertisers' ? (
        <AdvertiserQueue
          data={advertisers}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          showAll={showAll}
          onOpenAdvertiser={openAdvertiser}
          onHideFromReview={openHideFromReviewModal}
          sort={advertisersSortState.sort}
          dir={advertisersSortState.dir}
          toggleSort={advertisersSortState.toggleSort}
        />
      ) : (
        <MonthlyRowsView
          data={list}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          issueType={issueType}
          onSwitchToAdvertisers={() => setView('advertisers')}
          onOpenAdvertiser={openAdvertiser}
          sort={monthsSortState.sort}
          dir={monthsSortState.dir}
          toggleSort={monthsSortState.toggleSort}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Summary cards
// ---------------------------------------------------------------------------
// Five cards, daily-triage styling. The four issue cards are clickable
// filters; the fifth is an informational totals card with no hover affordance
// so operators don't try to click it. Accent stripes match the issue-type
// badge colours used elsewhere so the card visually echoes the chip it'll
// reveal in the table.
// Aligned with triage's FAMILY_COLORS so the two tabs read as one product:
// rejection-family blue for the headline cohort, drift amber for "elevated
// rate", acute red for "repeated / sustained".
const ISSUE_STRIPE: Record<string, string> = {
  commercial_impact: '#4F9EEC',
  high_rejection_rate: '#EBB617',
  repeated_high_rejection: '#E55353',
}

const SummaryCards = (props: {
  summary: IRejectionReviewSummary
  activeIssueType: string
  onApply: (next: { issueType?: string }) => void
}) => {
  const { summary, activeIssueType, onApply } = props
  return (
    <div className="anomalies-summary-grid m-t-4">
      <SummaryCard
        label="Total flagged"
        value={formatInteger(summary.totalFlaggedAdvertisers)}
        info="Advertisers with at least one issue in the selected months."
        active={activeIssueType === ''}
        onClick={() => onApply({ issueType: '' })}
      />
      <SummaryCard
        label="Commercial impact"
        value={formatInteger(summary.commercialImpactAdvertisers)}
        accent={ISSUE_STRIPE.commercial_impact}
        info="Months where rejected sale exceeds the commercial-impact threshold."
        active={activeIssueType === 'commercial_impact'}
        onClick={() => onApply({ issueType: 'commercial_impact' })}
      />
      <SummaryCard
        label="High rejection rate"
        value={formatInteger(summary.highRejectionRateAdvertisers)}
        accent={ISSUE_STRIPE.high_rejection_rate}
        info="Months where the sale-rejection rate is unusually elevated."
        active={activeIssueType === 'high_rejection_rate'}
        onClick={() => onApply({ issueType: 'high_rejection_rate' })}
      />
      <SummaryCard
        label="Repeated high rejection"
        value={formatInteger(summary.repeatedHighRejectionAdvertisers)}
        accent={ISSUE_STRIPE.repeated_high_rejection}
        info="≥2 of the last 3 completed months above the rate threshold."
        active={activeIssueType === 'repeated_high_rejection'}
        onClick={() => onApply({ issueType: 'repeated_high_rejection' })}
      />
      <TotalsCard
        sale={formatDkkLarge(summary.totalRejectedSaleDkk)}
        conv={formatInteger(summary.totalRejectedConversions)}
      />
    </div>
  )
}

const SummaryCard = (props: {
  label: string
  value: string
  accent?: string
  info?: string
  active?: boolean
  onClick?: () => void
}) => (
  <button
    type="button"
    className={varClass({
      'card': true,
      'anomalies-summary-card': true,
      'anomalies-summary-card-active': !!props.active,
    })}
    style={
      props.accent ? { borderTop: `3px solid ${props.accent}` } : undefined
    }
    onClick={props.onClick}
  >
    <div className="card-body p-y-2">
      <div className="text-light text-small">{props.label}</div>
      <div className="text-dark text-bigger text-bolder">{props.value}</div>
      {props.info && (
        <div className="anomalies-summary-card-hint">{props.info}</div>
      )}
    </div>
  </button>
)

// Informational totals — DKK + conversion count stacked. Not a filter, so
// rendered as a non-button to avoid suggesting interactivity.
const TotalsCard = (props: { sale: string; conv: string }) => (
  <div className="card anomalies-summary-card anomalies-summary-card-readonly">
    <div className="card-body p-y-2">
      <div className="text-light text-small">Totals (range)</div>
      <div className="text-dark text-bolder">{props.sale}</div>
      <div className="text-light text-small">
        {props.conv} rejected conv.
      </div>
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Filters card
// ---------------------------------------------------------------------------
// Layout mirrors the daily triage card: top row holds the date range + search
// + view toggle + reset (the things you reach for first), the NavFilter pill
// row below holds the segmenting filters (issue type / month status / device
// / show-all), and the rarely-used numeric thresholds tuck onto a third row.
const ISSUE_TYPE_FILTER_OPTIONS: Record<string, string> = {
  '': 'All issue types',
  commercial_impact: 'Commercial impact',
  high_rejection_rate: 'High rejection rate',
  repeated_high_rejection: 'Repeated high rejection',
}

const DEVICE_FILTER_OPTIONS: Record<string, string> = {
  '': 'All devices',
  Mobile: 'Mobile',
  Desktop: 'Desktop',
}

const SHOW_ALL_FILTER_OPTIONS: Record<string, string> = {
  '': 'Flagged only',
  all: 'Include without issues',
}

// '' maps to the backend default ('exclude'). Only 'rejections'-scoped
// suppressions are affected — daily-alert suppressions never touch this view.
const SUPPRESSED_FILTER_OPTIONS: Record<string, string> = {
  '': 'Hide suppressed',
  include: 'Include suppressed',
  only: 'Suppressed only',
}

type PillFilterEntry = { key: string; label: string; value: string }

const FiltersBar = (props: {
  startMonth: string
  endMonth: string
  onMonthRangeChange: (start: string, end: string) => void
  searchInput: string
  onSearch: (value: string) => void
  issueType: string
  onIssueTypeChange: (value: string) => void
  showAll: boolean
  onShowAllChange: (value: boolean) => void
  device: string
  onDeviceChange: (value: string) => void
  minSaleDkk: string
  onMinSaleDkkChange: (value: string) => void
  minSaleRate: string
  onMinSaleRateChange: (value: string) => void
  networkScope: NetworkScope
  onNetworkScopeChange: (next: NetworkScope) => void
  customNetworkUuids: string[]
  onCustomNetworkUuidsChange: (next: string[]) => void
  suppressedMode: TAnomaliesSuppressedMode
  onSuppressedModeChange: (next: TAnomaliesSuppressedMode) => void
  onReset: () => void
  view: View
  onViewChange: (view: View) => void
  presetValues: UserFilterValues
  applyPresetValues: (values: UserFilterValues) => void
  presetsState: ReturnType<typeof useUserFilters>
}) => {
  // Drive NavFilter from a derived array. The component is otherwise generic
  // — keys here line up with the local filter-options dicts above.
  const suppressedPillValue =
    props.suppressedMode === 'exclude' ? '' : props.suppressedMode

  const pillFilters: PillFilterEntry[] = [
    {
      key: 'issue_type',
      value: props.issueType,
      label: ISSUE_TYPE_FILTER_OPTIONS[props.issueType] || '',
    },
    {
      key: 'device',
      value: props.device,
      label: DEVICE_FILTER_OPTIONS[props.device] || '',
    },
    {
      key: 'show_all',
      value: props.showAll ? 'all' : '',
      label: props.showAll ? SHOW_ALL_FILTER_OPTIONS.all : '',
    },
    {
      key: 'suppressed',
      value: suppressedPillValue,
      label: suppressedPillValue
        ? SUPPRESSED_FILTER_OPTIONS[suppressedPillValue]
        : '',
    },
  ]

  const handlePillChange = (next: PillFilterEntry[]) => {
    for (const f of next) {
      switch (f.key) {
        case 'issue_type':
          if (f.value !== props.issueType) props.onIssueTypeChange(f.value)
          break
        case 'device':
          if (f.value !== props.device) props.onDeviceChange(f.value)
          break
        case 'show_all': {
          const want = f.value === 'all'
          if (want !== props.showAll) props.onShowAllChange(want)
          break
        }
        case 'suppressed': {
          const want: TAnomaliesSuppressedMode =
            f.value === 'only' || f.value === 'include' ? f.value : 'exclude'
          if (want !== props.suppressedMode) props.onSuppressedModeChange(want)
          break
        }
      }
    }
  }

  return (
    <div className="card anomalies-rejection-review-filters m-t-4">
      <div className="card-body">
        <div
          className="row row-center row-space-between"
          style={{ gap: 12, flexWrap: 'wrap' }}
        >
          <div className="row" style={{ gap: 16, alignItems: 'flex-end' }}>
            <PresetsControl
              scope="rejection_review"
              currentValues={props.presetValues}
              onApply={props.applyPresetValues}
              presetsState={props.presetsState}
            />
            <div className="row row-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <label className="text-light text-small">
                From{' '}
                <input
                  type="month"
                  value={props.startMonth}
                  onChange={(e) =>
                    props.onMonthRangeChange(e.target.value, props.endMonth)
                  }
                />
              </label>
              <label className="text-light text-small">
                To{' '}
                <input
                  type="month"
                  value={props.endMonth}
                  onChange={(e) =>
                    props.onMonthRangeChange(props.startMonth, e.target.value)
                  }
                />
              </label>
              <div className="control" style={{ width: 220 }}>
                <input
                  type="text"
                  placeholder="Search advertiser…"
                  value={props.searchInput}
                  onChange={(e) => props.onSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="row" style={{ gap: 16, alignItems: 'flex-end' }}>
            <FilterGroup label="Networks">
              <NetworkScopePill
                value={props.networkScope}
                onChange={props.onNetworkScopeChange}
                customUuids={props.customNetworkUuids}
                onCustomUuidsChange={props.onCustomNetworkUuidsChange}
              />
            </FilterGroup>
            <FilterGroup label="View">
              <ViewToggle view={props.view} onChange={props.onViewChange} />
            </FilterGroup>
            <button
              type="button"
              className="anomalies-faq-toggle"
              onClick={props.onReset}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="row m-t-3" style={{ gap: 16, alignItems: 'flex-end' }}>
          <FilterGroup label="Filters" grow>
            <NavFilter
              filters={pillFilters}
              setFilters={handlePillChange}
              filterLabels={{
                issue_type: 'Issue type',
                device: 'Device',
                show_all: 'Coverage',
                suppressed: 'Suppressed',
              }}
              filterOptions={{
                issue_type: ISSUE_TYPE_FILTER_OPTIONS,
                device: DEVICE_FILTER_OPTIONS,
                show_all: SHOW_ALL_FILTER_OPTIONS,
                suppressed: SUPPRESSED_FILTER_OPTIONS,
              }}
              hideAdd
              hideRemove
              hideChecks={false}
            />
          </FilterGroup>
        </div>

        <div
          className="row row-center m-t-2 text-light text-small"
          style={{ gap: 16, flexWrap: 'wrap' }}
        >
          <label className="row row-center" style={{ gap: 6 }}>
            Min rejected sale (DKK)
            <input
              type="number"
              min={0}
              step={100}
              style={{ width: 110 }}
              value={props.minSaleDkk}
              onChange={(e) => props.onMinSaleDkkChange(e.target.value)}
            />
          </label>
          <label className="row row-center" style={{ gap: 6 }}>
            Min rejected sale %
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              style={{ width: 70 }}
              value={props.minSaleRate}
              onChange={(e) => props.onMinSaleRateChange(e.target.value)}
            />
          </label>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// View toggle (segmented control — inlined into the filters bar)
// ---------------------------------------------------------------------------
const ViewToggle = (props: { view: View; onChange: (v: View) => void }) => {
  const items: { value: View; label: string }[] = [
    { value: 'advertisers', label: 'Advertisers' },
    { value: 'months', label: 'Monthly rows' },
  ]
  return (
    <div className="anomalies-rejection-review-view-toggle">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          className={varClass({
            'anomalies-rejection-review-view-toggle-btn': true,
            active: props.view === item.value,
          })}
          onClick={() => props.onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Advertiser queue
// ---------------------------------------------------------------------------
const AdvertiserQueue = (props: {
  data: IRejectionReviewAdvertisersResponse | null
  page: number
  setPage: (p: number) => void
  pageSize: number
  onPageSizeChange: (size: number) => void
  showAll: boolean
  onOpenAdvertiser: (advertiserDomain: string, e: React.MouseEvent) => void
  onHideFromReview: (advertiserDomain: string) => void
  sort: string
  dir: 'asc' | 'desc'
  toggleSort: (field: string, defaultDir: 'asc' | 'desc') => () => void
}) => {
  const {
    data,
    page,
    setPage,
    pageSize,
    onPageSizeChange,
    showAll,
    onOpenAdvertiser,
    onHideFromReview,
    sort,
    dir,
    toggleSort,
  } = props
  const rows = data?.rows || []
  const headProps = { sort, dir, toggleSort }

  const sortedRows = useMemo(() => {
    const sign = dir === 'desc' ? -1 : 1
    const get = (r: IRejectionReviewAdvertiserQueueRow): unknown => {
      switch (sort) {
        case 'advertiserDomain':
          return r.advertiserDomain
        case 'totalRejectedSaleDkk':
          return r.totalRejectedSaleDkk
        case 'totalRejectedConversions':
          return r.totalRejectedConversions
        case 'avgRejectedSaleRate':
          return r.totalSaleDkk > 0 ? r.totalRejectedSaleDkk / r.totalSaleDkk : null
        case 'avgRejectedConversionRate':
          return r.totalConversions > 0
            ? r.totalRejectedConversions / r.totalConversions
            : null
        case 'maxRejectedSaleRate':
          return r.maxRejectedSaleRate
        case 'maxRejectedConversionRate':
          return r.maxRejectedConversionRate
        case 'affectedMonths':
          return r.affectedMonths
        case 'worstMonth':
          return r.worstMonth
        default:
          return null
      }
    }
    return [...rows].sort((a, b) => {
      const av = get(a)
      const bv = get(b)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign
      return String(av).localeCompare(String(bv)) * sign
    })
  }, [rows, sort, dir])

  // Client-side pagination over the full sorted set.
  const total = sortedRows.length
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1
  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize]
  )

  // Summary over the full filtered set (not just the visible page). Rates are
  // aggregate (sum/sum), not an average of per-row percentages, so they read
  // as "of everything in this queue, X% was rejected".
  const summary = useMemo(() => {
    let rejSale = 0
    let totalSale = 0
    let rejConv = 0
    let totalConv = 0
    let worstSaleRate: number | null = null
    for (const r of rows) {
      rejSale += r.totalRejectedSaleDkk ?? 0
      totalSale += r.totalSaleDkk ?? 0
      rejConv += r.totalRejectedConversions ?? 0
      totalConv += r.totalConversions ?? 0
      if (
        r.maxRejectedSaleRate != null &&
        (worstSaleRate == null || r.maxRejectedSaleRate > worstSaleRate)
      ) {
        worstSaleRate = r.maxRejectedSaleRate
      }
    }
    return {
      rejSale,
      rejConv,
      saleRate: totalSale > 0 ? rejSale / totalSale : null,
      convRate: totalConv > 0 ? rejConv / totalConv : null,
      worstSaleRate,
    }
  }, [rows])

  return (
    <div className="card m-t-4">
      <div className="card-body">
        <Loadable data={data}>
          {rows.length === 0 && data ? (
            <EmptyState showAll={showAll} />
          ) : (
            <div className="table-container-overflow">
              <table className="table table-bordered table-hoverable table-sortable anomalies-rejection-review-table">
                <thead>
                  <tr>
                    <TableHead value="advertiserDomain" label="Advertiser" {...headProps} />
                    <th>Issues</th>
                    <TableHead value="affectedMonths" label="Months" align="right" desc {...headProps} />
                    <TableHead value="totalRejectedSaleDkk" label="Rejected sale" align="right" desc {...headProps} />
                    <TableHead value="avgRejectedSaleRate" label="Avg sale %" align="right" desc {...headProps} />
                    <TableHead value="totalRejectedConversions" label="Rejected conv." align="right" desc {...headProps} />
                    <TableHead value="avgRejectedConversionRate" label="Avg conv. %" align="right" desc {...headProps} />
                    <TableHead value="worstMonth" label="Worst month" desc {...headProps} />
                    <TableHead value="maxRejectedSaleRate" label="Worst sale %" align="right" desc {...headProps} />
                    <TableHead value="maxRejectedConversionRate" label="Worst conv. %" align="right" desc {...headProps} />
                    <th className="text-right" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => (
                    <AdvertiserQueueRow
                      key={row.advertiserDomain}
                      row={row}
                      onOpenAdvertiser={onOpenAdvertiser}
                      onHideFromReview={onHideFromReview}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="anomalies-rejection-review-summary-row">
                    <td className="text-dark text-bold">Total</td>
                    <td />
                    <td />
                    <td className="text-right text-bold">{formatDkkWhole(summary.rejSale)}</td>
                    <td className="text-right text-bold">{formatPercent1(summary.saleRate)}</td>
                    <td className="text-right text-bold">{formatInteger(summary.rejConv)}</td>
                    <td className="text-right text-bold">{formatPercent1(summary.convRate)}</td>
                    <td />
                    <td className="text-right text-bold">{formatPercent1(summary.worstSaleRate)}</td>
                    <td />
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Loadable>

        {total > 0 && (
          <div className="row row-space-between row-center m-t-3">
            <div className="text-light text-small">
              {total} advertiser{total === 1 ? '' : 's'}
            </div>
            <div className="row row-center" style={{ gap: 12 }}>
              <Pagination page={page} setPage={setPage} maxPage={totalPages} />
              <PageSizeSelect
                value={pageSize}
                onChange={onPageSizeChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const PageSizeSelect = (props: {
  value: number
  onChange: (size: number) => void
}) => (
  <label className="text-light text-small row row-center" style={{ gap: 6 }}>
    Page size
    <select
      className="anomalies-page-size-select"
      value={props.value}
      onChange={(e) => props.onChange(parseInt(e.target.value, 10))}
    >
      {[10, 25, 50, 100, 200].map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  </label>
)

const EmptyState = (props: { showAll: boolean }) => {
  if (props.showAll) {
    return (
      <div className="text-light text-center p-y-10">
        No advertisers match the selected filters.
      </div>
    )
  }
  return (
    <div className="text-light text-center p-y-10">
      No advertisers need rejection review for the selected months.
    </div>
  )
}

// Period = a run of consecutive flagged months. Used in the detail page's
// advanced section to collapse "Feb–Apr 2026 was a rough stretch" into a
// single header rather than three loose rows. Returned periods are
// date-ascending; months inside each period stay date-ascending too.
export type RejectionPeriod = {
  startMonth: string // 'YYYY-MM-01'
  endMonth: string
  monthCount: number
  totalRejectedSaleDkk: number
  totalRejectedConversions: number
  totalSaleDkk: number
  totalConversions: number
  worstSaleRate: number | null
  issueTypes: TRejectionReviewIssueType[]
  months: IRejectionReviewAdvertiserMonth[]
}

export const groupRejectionPeriods = (
  months: IRejectionReviewAdvertiserMonth[]
): RejectionPeriod[] => {
  const sorted = [...months].sort((a, b) =>
    a.conversionMonth.localeCompare(b.conversionMonth)
  )
  const periods: RejectionPeriod[] = []
  let run: IRejectionReviewAdvertiserMonth[] = []
  const flush = () => {
    if (run.length === 0) return
    const first = run[0]
    const last = run[run.length - 1]
    let totalRejSale = 0
    let totalRejConv = 0
    let totalSale = 0
    let totalConv = 0
    let worstSale: number | null = null
    const types = new Set<TRejectionReviewIssueType>()
    for (const m of run) {
      totalRejSale += m.rejectedSaleDkk ?? 0
      totalRejConv += m.rejectedConversions ?? 0
      totalSale += m.totalSaleDkk ?? 0
      totalConv += m.totalConversions ?? 0
      if (
        m.rejectedSaleRate != null &&
        (worstSale == null || m.rejectedSaleRate > worstSale)
      ) {
        worstSale = m.rejectedSaleRate
      }
      for (const t of m.issueTypes || []) types.add(t)
    }
    periods.push({
      startMonth: first.conversionMonth,
      endMonth: last.conversionMonth,
      monthCount: run.length,
      totalRejectedSaleDkk: totalRejSale,
      totalRejectedConversions: totalRejConv,
      totalSaleDkk: totalSale,
      totalConversions: totalConv,
      worstSaleRate: worstSale,
      issueTypes: Array.from(types),
      months: run.slice(),
    })
    run = []
  }
  for (const m of sorted) {
    if ((m.issueTypes || []).length > 0) {
      run.push(m)
    } else {
      flush()
    }
  }
  flush()
  return periods
}

// Pick the month to summarise in the queue row header.
const pickWorstMonth = (row: IRejectionReviewAdvertiserQueueRow) => {
  if (row.worstMonth) {
    const exact = row.months.find((m) => m.conversionMonth === row.worstMonth)
    if (exact) return exact
  }
  let worst = row.months[0]
  for (const m of row.months) {
    if ((m.rejectedSaleRate ?? -1) > (worst?.rejectedSaleRate ?? -1)) worst = m
  }
  return worst
}

const AdvertiserQueueRow = (props: {
  row: IRejectionReviewAdvertiserQueueRow
  onOpenAdvertiser: (advertiserDomain: string, e: React.MouseEvent) => void
  onHideFromReview: (advertiserDomain: string) => void
}) => {
  const { row, onOpenAdvertiser, onHideFromReview } = props
  const worst = pickWorstMonth(row)
  const worstMonthLabel = worst ? formatMonth(worst.conversionMonth) : null
  const handleClick = (e: React.MouseEvent) => onOpenAdvertiser(row.advertiserDomain, e)

  return (
    <tr className="anomalies-rejection-review-row-clickable" onClick={handleClick}>
      <td>
        <a
          className="text-dark text-bold anomalies-advertiser-link"
          href={`/anomalies/rejection-review/${encodeURIComponent(row.advertiserDomain)}`}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
            e.preventDefault()
            e.stopPropagation()
            onOpenAdvertiser(row.advertiserDomain, e)
          }}
        >
          {row.advertiserDomain}
        </a>
        {row.reasonText && (
          <div className="text-light anomalies-rejection-row-sub">
            {europeanizeNumbers(row.reasonText)}
          </div>
        )}
      </td>
      <td>
        <span className="row row-center" style={{ gap: 4, flexWrap: 'wrap' }}>
          <IssueTypeBadges issueTypes={row.issueTypes} />
          {worst && deriveMaturity(worst.daysSinceMonthEnd) === 'open' && (
            <OpenMonthBadge />
          )}
        </span>
      </td>
      <td className="text-right">{formatInteger(row.affectedMonths)}</td>
      <td className="text-right text-bold">{formatDkkWhole(row.totalRejectedSaleDkk)}</td>
      <td className="text-right">
        {formatPercent1(
          row.totalSaleDkk > 0 ? row.totalRejectedSaleDkk / row.totalSaleDkk : null
        )}
      </td>
      <td className="text-right">{formatInteger(row.totalRejectedConversions)}</td>
      <td className="text-right">
        {formatPercent1(
          row.totalConversions > 0
            ? row.totalRejectedConversions / row.totalConversions
            : null
        )}
      </td>
      <td>{worstMonthLabel || '—'}</td>
      <td className="text-right">{formatPercent1(row.maxRejectedSaleRate)}</td>
      <td className="text-right">{formatPercent1(row.maxRejectedConversionRate)}</td>
      <td className="text-right" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="anomalies-row-suppress"
          onClick={() => onHideFromReview(row.advertiserDomain)}
          title={`Hide ${row.advertiserDomain} from the rejection review. Daily alerts are unaffected.`}
        >
          Hide
        </button>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Monthly rows view (secondary)
// ---------------------------------------------------------------------------
const MonthlyRowsView = (props: {
  data: IRejectionReviewListResponse | null
  page: number
  setPage: (p: number) => void
  pageSize: number
  onPageSizeChange: (size: number) => void
  issueType: string
  onSwitchToAdvertisers: () => void
  onOpenAdvertiser: (advertiserDomain: string, e: React.MouseEvent) => void
  sort: string
  dir: 'asc' | 'desc'
  toggleSort: (field: string, defaultDir: 'asc' | 'desc') => () => void
}) => {
  const {
    data,
    page,
    setPage,
    pageSize,
    onPageSizeChange,
    issueType,
    onSwitchToAdvertisers,
    onOpenAdvertiser,
    sort,
    dir,
    toggleSort,
  } = props
  const rows = data?.rows || []
  const headProps = { sort, dir, toggleSort }
  const sortedRows = useMemo(() => {
    const sign = dir === 'desc' ? -1 : 1
    return [...rows].sort((a, b) => {
      const av = (a as any)[sort]
      const bv = (b as any)[sort]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign
      return String(av).localeCompare(String(bv)) * sign
    })
  }, [rows, sort, dir])

  // Client-side pagination over the full sorted set.
  const total = sortedRows.length
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1
  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize]
  )

  return (
    <div className="card m-t-4">
      <div className="card-body">
        <Loadable data={data}>
          <div className="table-container-overflow">
            <table className="table table-bordered table-hoverable table-sortable anomalies-rejection-review-table">
              <thead>
                <tr>
                  <TableHead value="advertiserDomain" label="Advertiser" {...headProps} />
                  <th>Issues</th>
                  <TableHead value="conversionMonth" label="Month" desc {...headProps} />
                  <TableHead value="rejectedSaleDkk" label="Rejected sale" align="right" desc {...headProps} />
                  <TableHead value="totalSaleDkk" label="Total sale" align="right" desc {...headProps} />
                  <TableHead value="rejectedSaleRate" label="Rej. sale %" align="right" desc {...headProps} />
                  <TableHead value="rejectedConversions" label="Rej. conv." align="right" desc {...headProps} />
                  <TableHead value="totalConversions" label="Total conv." align="right" desc {...headProps} />
                  <TableHead value="rejectedConversionRate" label="Rej. conv. %" align="right" desc {...headProps} />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <MonthlyRow
                    key={`${row.advertiserDomain}_${row.conversionMonth}`}
                    row={row}
                    onClickAdvertiser={(e) => onOpenAdvertiser(row.advertiserDomain, e)}
                  />
                ))}
                {rows.length === 0 && data && (
                  <tr>
                    <td colSpan={9} className="text-center text-light p-y-10">
                      {issueType === 'repeated_high_rejection' ? (
                        <>
                          “Repeated high rejection” is an advertiser-level
                          signal (≥2 of the last 3 completed months flagged)
                          and isn’t carried on individual month rows.{' '}
                          <button
                            type="button"
                            className="anomalies-detail-range-btn"
                            onClick={onSwitchToAdvertisers}
                          >
                            Switch to Advertisers view
                          </button>
                        </>
                      ) : (
                        'No months match these filters.'
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Loadable>

        {total > 0 && (
          <div className="row row-space-between row-center m-t-3">
            <div className="text-light text-small">
              {total} row{total === 1 ? '' : 's'}
            </div>
            <div className="row row-center" style={{ gap: 12 }}>
              <Pagination page={page} setPage={setPage} maxPage={totalPages} />
              <PageSizeSelect
                value={pageSize}
                onChange={onPageSizeChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const MonthlyRow = (props: {
  row: IRejectionReviewMonthRow
  onClickAdvertiser: (e: React.MouseEvent) => void
}) => {
  const { row, onClickAdvertiser } = props
  const advertiserHref = `/anomalies/rejection-review/${encodeURIComponent(row.advertiserDomain)}`
  return (
    <tr>
      <td>
        <a
          className="text-dark text-bold anomalies-advertiser-link"
          href={advertiserHref}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
            e.preventDefault()
            onClickAdvertiser(e)
          }}
        >
          {row.advertiserDomain}
        </a>
      </td>
      <td>
        <IssueTypeBadges issueTypes={row.issueTypes} />
      </td>
      <td>
        <span className="row row-center" style={{ gap: 6 }}>
          {formatMonth(row.conversionMonth)}
          {deriveMaturity(row.daysSinceMonthEnd) === 'open' && (
            <OpenMonthBadge />
          )}
        </span>
      </td>
      <td className="text-right">{formatDkkWhole(row.rejectedSaleDkk)}</td>
      <td className="text-right text-light">{formatDkkWhole(row.totalSaleDkk)}</td>
      <td className="text-right text-bold">{formatPercent1(row.rejectedSaleRate)}</td>
      <td className="text-right">{formatInteger(row.rejectedConversions)}</td>
      <td className="text-right text-light">{formatInteger(row.totalConversions)}</td>
      <td className="text-right">{formatPercent1(row.rejectedConversionRate)}</td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------
// Only the current month gets a status badge — past months are all "completed"
// now (no settled-vs-cooling distinction) so labelling each row "Completed"
// would just be noise. The Open badge signals "this is preliminary, totals
// are still growing".
export const OpenMonthBadge = () => (
  <span className="anomalies-rejection-maturity-badge anomalies-rejection-maturity-open">
    Open
  </span>
)

export const IssueTypeBadges = (props: {
  issueTypes: TRejectionReviewIssueType[] | null | undefined
}) => {
  const list = props.issueTypes || []
  if (list.length === 0) {
    return <span className="text-light text-small">—</span>
  }
  return (
    <span
      className="row row-center"
      style={{ gap: 4, flexWrap: 'wrap', display: 'inline-flex' }}
    >
      {list.map((t) => (
        <span
          key={t}
          className={varClass({
            'anomalies-rejection-issue-badge': true,
            [`anomalies-rejection-issue-${t}`]: true,
          })}
        >
          {ISSUE_TYPE_LABELS[t]}
        </span>
      ))}
    </span>
  )
}

// Re-export local formatters so the modal can match the table styling.
export {
  formatMonth as formatRejectionReviewMonth,
  formatPercent1 as formatRejectionPercent,
  formatDkkWhole as formatRejectionDkk,
}
